import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Animate from "../Animate";
import useProductForm from "@/hooks/useProductForm";
import {createPortal} from "react-dom";
import { formatMoney } from "@/utils/formatMoney";
import {product as productStore} from "@/store/Product";
import {price as priceStore} from "@/store/Price";
import {observer} from "mobx-react";
import {fetchAddItem} from "@/services/cart";
import {triggerEvent} from "@/utils/custom-events";
import {translateRecharge} from "@/utils/translate-recharge";

const StickyProductBar = observer(
  ({product, sectionId, image, title, description}) => {
    const {t} = useTranslation();

    const [showProductBar, setIsVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const {hasSubscription, getSellingPlan} = useProductForm({
      product,
      formId: sectionId,
    });

    const {variantId: id, sellingPlanId, quantity} = productStore;
    const priceData = priceStore?.currentPrice;
    const subscriptionQtd1 = priceStore?.pricesQtd1.subscriptionPrice;

    const sellingPlanOptions = getSellingPlan(sellingPlanId)?.options;
    const sellingPlanLabel = translateRecharge(
      (sellingPlanOptions &&
        (sellingPlanOptions[1]
          ? sellingPlanOptions[1].value
          : sellingPlanOptions[0].value)) ??
        "",
    );

    const addToCartLabel = `${t("action.add_to_cart")} • ${formatMoney(priceData?.price)} ${hasSubscription ? (sellingPlanId ? `/ ${sellingPlanLabel}` : "") : ""}`;

    const ref = useRef(null);

    const onSubmit = async e => {
      e.preventDefault();
      setLoading(true);

      await fetchAddItem({id, quantity, selling_plan: sellingPlanId});
      triggerEvent("cart:modal:toggle", {
        open: true,
      });
      setLoading(false);
    };

    useEffect(() => {
      if (!("IntersectionObserver" in window)) return undefined;

      const containers = document.querySelectorAll(
        ".section-main-product, footer",
      );

      const observer = new IntersectionObserver(
        entries => {
          const [entry] = entries;
          setIsVisible(!entry.isIntersecting);
        },
        {
          root: null,
          rootMargin: "-10px",
          threshold: 0.6,
        },
      );

      containers.forEach(container => {
        if (!container) return;
        observer.observe(container);
      });

      return () => {
        containers.forEach(container => {
          if (!container) return;
          observer.unobserve(container);
        });
      };
    }, []);

    useEffect(() => {
      let chatButtonEl;
      let styleElement;

      const updateChatStyles = (showBar) => {
        // Remove existing style element if it exists
        if (styleElement) {
          styleElement.remove();
        }

        // Create new style element
        styleElement = document.createElement('style');
        styleElement.textContent = `
          iframe[id="chat-button"],
          iframe[id="chat-window"] {
            transition: transform 0.3s ease-in-out;
            transform: ${showBar ? 'translateY(-70px)' : 'none'};
          }
        `;
        document.head.appendChild(styleElement);
      };

      // Handle initial chat elements position
      updateChatStyles(showProductBar);

      // Handle chat button click events
      const onClickChatButton = () => {
        setTimeout(() => {
          updateChatStyles(showProductBar);
        }, 300);
      };

      const interval = setInterval(() => {
        chatButtonEl = document.querySelector(
          'iframe[id="chat-button"]',
        )?.contentDocument;

        if (chatButtonEl) {
          chatButtonEl?.addEventListener("click", onClickChatButton);
          clearInterval(interval);
        }
      }, 500);

      return () => {
        if (styleElement) {
          styleElement.remove();
        }
        chatButtonEl?.removeEventListener("click", onClickChatButton);
        clearInterval(interval);
      };
    }, [showProductBar]);

    return createPortal(
      <Animate in={showProductBar} nodeRef={ref}>
        <form ref={ref} onSubmit={onSubmit}>
          <div className="fixed bottom-0 z-[8] w-full rounded-t-2xl bg-dark-green p-4 pb-8 text-white md:rounded-t-3xl md:pb-4">
            <div className="mx-auto max-w-[90rem] !px-0 md:flex md:justify-between md:gap-4">
              <div className="hidden gap-4 md:flex">
                {(image || product.featured_image) && (
                  <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full bg-white">
                    <img
                      className="image object-cover"
                      src={image?.src ?? product.featured_image.src}
                      alt={
                        image?.alt ??
                        product.featured_image.alt ??
                        product.title
                      }
                    />
                  </div>
                )}

                <div className="flex flex-col">
                  <h3 className="text-2xl font-normal not-italic leading-[120%]">
                    {title ? title : product.title}
                  </h3>
                  <div className="line-clamp-1 max-w-[41.5rem] text-ellipsis text-sm font-normal not-italic leading-[140%] tracking-[0.00875rem]">
                    {description ? description : product.description_stripped}
                  </div>
                </div>
              </div>

              <button
                disabled={loading}
                className="button button--primary w-full md:w-fit"
              >
                {addToCartLabel}
              </button>
              {!!sellingPlanId && (
                <p className="mt-2 flex justify-center text-center text-[13px] font-normal not-italic leading-[130%] text-white md:hidden">
                  {t("product.sticky_bar.mobile_msg", {
                    percentage: parseFloat(
                      (
                        100 -
                        (100 * subscriptionQtd1.price) /
                          (subscriptionQtd1.compareAtPrice ?? 1)
                      ).toFixed(0),
                    ),
                    savings: formatMoney(
                      quantity * subscriptionQtd1.compareAtPrice -
                        quantity * subscriptionQtd1.price,
                    ),
                  })}
                </p>
              )}
            </div>
          </div>
        </form>
      </Animate>,

      document.body,
    );
  },
);

export default StickyProductBar;
